import * as React from 'react';
import styled from 'styled-components';
import {FileObject, FileType, getFilePath} from 'api/library';
import {DocumentValue} from 'api/lesson/types';
import CardContext from '../CardContext';
import LessonBuilderContext
  from 'components/LessonBuilder/LessonBuilderContext';
import FileContainer, {ControlsPosition} from './components/FileContainer';
import Upload, {UploadVariant} from 'components/Upload';
import ButtonLink from 'components/ButtonLink';
import {ButtonVariant} from 'components/Button';
import getDocumentIcon from 'helpers/get-document-icon';

export interface DocumentTemplateProps {
  value?: DocumentValue;
}

const Container = styled.div`
  align-items: center;
  grid-template-columns: max-content 1fr max-content;
  display: grid;
  
  padding: 12px 55px 12px 8px;
  border-radius: 5px;
  background-color: ${({theme}) => `
    ${theme.variables.backgroundColor};
  `};
`;

const IconContainer = styled.div`
  margin-right: 16px;
`;

const Title = styled.span`
  line-height: 20px;
  font-weight: 500;
  font-size: 16px;
`;

/**
 * This component renders the document template.
 * @param {DocumentTemplateProps} props
 * @return {React.ReactElement<DocumentTemplateProps>}
 * @constructor
 */
export default function DocumentTemplate(
    props: DocumentTemplateProps,
): React.ReactElement<DocumentTemplateProps> {
  const cardContext = React.useContext(CardContext);
  const {lesson, setLesson} = React.useContext(LessonBuilderContext);
  const content = lesson?.content || [];

  /**
   * Set the value.
   * @param {DocumentTemplateProps<value>} value
   */
  function setValue(value: DocumentTemplateProps['value']): void {
    if (cardContext.index !== undefined) {
      content[cardContext.index].value = value;

      (lesson && setLesson) && setLesson({
        ...lesson,
        content,
      });
    }
  }

  /**
   * Is the video empty.
   * @return {boolean}
   */
  function isEmpty(): boolean {
    return !Boolean(props.value);
  }

  /**
   * Render preview.
   * @return {JSX.Element | undefined}
   */
  function renderPreview(): JSX.Element | undefined {
    /**
     * This delete event handler.
     */
    function handleDelete(): void {
      setValue(undefined);
    }

    return props.value ? (
      <FileContainer
        id={props.value}
        onDelete={handleDelete}
        controlsPosition={ControlsPosition.Center}
      >
        {(file) => (
          <Container>
            <IconContainer>
              {getDocumentIcon(file.meta)}
            </IconContainer>
            <Title>
              {file.title}
            </Title>
            <ButtonLink
              target="_blank"
              rel="noopener noreferrer"
              href={getFilePath(file.id)}
              variant={ButtonVariant.Outline}
            >
              Посмотреть
            </ButtonLink>
          </Container>
        )}
      </FileContainer>
    ) : undefined;
  }

  /**
   * Render upload.
   * @return {JSX.Element}
   */
  function renderUpload(): JSX.Element {
    /**
     * This upload event handler.
     * @param {FileObject} file
     */
    function handleUpload(file: FileObject): void {
      setValue(file.id);
    }

    /**
     * This select event handler.
     * @param {FileObject[]} attached
     */
    function handleSelect(attached: FileObject[]): void {
      setValue(attached[0].id);
    }

    return (
      <Upload
        type={FileType.Document}
        variant={UploadVariant.Accent}
        onUpload={handleUpload}
        picker={{
          onSelect: handleSelect,
        }}
      />
    );
  }

  return (
    <React.Fragment>
      {!isEmpty() ? (
        renderPreview()
      ) : (
        renderUpload()
      )}
    </React.Fragment>
  );
}
